import {
  ApiRuleRegistryResponse,
  ApiRuleConditionTypeInfo,
  ApplyToRequest,
  Rule,
  RuleConditionContext,
  RuleConditionsItems,
  RuleGroup,
  RuleList,
  RuleShort,
} from "@/types/rule.types.ts";
import { useQueries, useQuery } from "@tanstack/react-query";
import { apiRule } from "@/api/rule.api.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { ApiErrorException } from "@/types/api.types.ts";
import { RuleRegistryPlaceKey } from "@/types/rule-whitelists.ts";
import { SelectItem } from "@skailer-com/uikit";
import { useMemo } from "react";
import i18n from "@/i18n.ts";
import {
  ApiWorkflowEventTypes,
  ApiWorkflowTriggerType,
  isCandidateWorkflowEventType,
} from "@/types/workflows/workflow.base-enum.types.ts";

export const getUnavailableRuleContextsForSubject = (
  eventType: ApiWorkflowEventTypes | null | undefined
): RuleConditionContext[] =>
  isCandidateWorkflowEventType(eventType)
    ? [RuleConditionContext.affected_employee]
    : [RuleConditionContext.candidate_job];

const getFallbackConditionTypeTitle = ({
  condition_type,
  title_key,
}: ApiRuleRegistryResponse["places"][number]["condition_types"][number]): string => {
  if (i18n.exists(title_key)) return i18n.t(title_key);

  return condition_type.replaceAll("_", " ");
};

export const rulesKeys = createQueryKeys("rules", {
  registry: {
    queryKey: null,
    queryFn: async () => {
      const result = await apiRule.getRegistry();
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  },
  calcApplyTo: (payload: ApplyToRequest) => ({
    queryKey: [payload],
    queryFn: async ({ signal }) => {
      const result = await apiRule.calcApplyTo(payload, signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
});

export const getRuleConditionTypesForPlace = (
  registry: ApiRuleRegistryResponse | undefined,
  placeKey: RuleRegistryPlaceKey,
  workflowTriggerType?: ApiWorkflowTriggerType,
  extraUnavailableContexts?: RuleConditionContext[]
): SelectItem[] => {
  const translatedTypes = new Map(
    RuleConditionsItems().map((conditionType) => [String(conditionType.id), conditionType])
  );
  const place = registry?.places.find(({ key }) => key === placeKey);
  const unavailableContexts = new Set([
    ...(workflowTriggerType
      ? (registry?.unavailable_contexts_by_workflow_trigger_type?.[workflowTriggerType] ?? [])
      : []),
    ...(extraUnavailableContexts ?? []),
  ]);

  return (place?.condition_types ?? [])
    .filter((conditionType) => !unavailableContexts.has(conditionType.context))
    .map((conditionType) => {
      const translatedType = translatedTypes.get(conditionType.condition_type);

      return (
        translatedType ?? {
          id: conditionType.condition_type,
          title: getFallbackConditionTypeTitle(conditionType),
        }
      );
    });
};

export const useRuleConditionTypes = (
  placeKey: RuleRegistryPlaceKey,
  workflowTriggerType?: ApiWorkflowTriggerType,
  extraUnavailableContexts?: RuleConditionContext[]
) => {
  const query = useQuery({ ...rulesKeys.registry, staleTime: Infinity });

  const conditionTypes = useMemo(
    () =>
      getRuleConditionTypesForPlace(
        query.data,
        placeKey,
        workflowTriggerType,
        extraUnavailableContexts
      ),
    [placeKey, query.data, workflowTriggerType, extraUnavailableContexts]
  );
  const unavailableContexts = new Set([
    ...(workflowTriggerType
      ? (query.data?.unavailable_contexts_by_workflow_trigger_type?.[workflowTriggerType] ?? [])
      : []),
    ...(extraUnavailableContexts ?? []),
  ]);
  const conditionTypeInfos: ApiRuleConditionTypeInfo[] =
    query.data?.places
      .find(({ key }) => key === placeKey)
      ?.condition_types.filter(({ context }) => !unavailableContexts.has(context)) ?? [];

  return {
    conditionTypes,
    conditionTypeInfos,
    isLoading: query.isLoading,
    isError: query.isError,
  };
};

export const useCalcApplyTo = (payload: RuleList, enabled = true) => {
  return useQuery({
    ...rulesKeys.calcApplyTo(payload),
    enabled: enabled && payload.rules.length > 0,
  });
};

export const useCalcApplyToGroups = (
  groups: RuleGroup[],
  enabled = true,
  assignToGroups?: RuleGroup[]
) => {
  const nonEmptyGroups = groups.filter((group) => group.rules.length > 0);
  const payload: ApplyToRequest = {
    groups: nonEmptyGroups,
    ...(assignToGroups ? { assign_to_groups: assignToGroups } : {}),
  };

  const query = useQuery({
    ...rulesKeys.calcApplyTo(payload),
    enabled: enabled && nonEmptyGroups.length > 0,
  });

  return {
    employeeIds: query.data?.employee_ids ?? [],
    isLoading: query.isLoading,
    hasRules: nonEmptyGroups.length > 0,
  };
};

export const useRuleGroupsPreview = (
  groups: RuleGroup[],
  place: RuleRegistryPlaceKey,
  enabled = true,
  assignToGroups?: RuleGroup[],
  additionalHiddenRules: RuleShort[] = []
) => {
  const hiddenRules: Rule[] = additionalHiddenRules.map((rule) => ({
    ...rule,
    condition_entities: [],
    value_entities: [],
  }));
  const payload: ApplyToRequest = {
    groups: groups.map((group) => ({
      ...group,
      rules: group.rules.length ? [...hiddenRules, ...group.rules] : [],
    })),
    place,
    ...(assignToGroups !== undefined ? { assign_to_groups: assignToGroups } : {}),
  };
  const hasRules = groups.some((group) => group.rules.length > 0);
  const query = useQuery({
    ...rulesKeys.calcApplyTo(payload),
    enabled: enabled && hasRules,
  });
  const isError =
    query.isError || (query.isSuccess && assignToGroups === undefined && !query.data?.funnel);

  return {
    employeeIds: query.data?.employee_ids ?? [],
    funnel: query.data?.funnel,
    total: query.data?.funnel?.employees_count ?? query.data?.employee_ids.length,
    isLoading: query.isFetching,
    isError,
    hasRules,
    refetch: query.refetch,
  };
};

export const useCalcApplyToRules = (
  rules: Rule[],
  enabled = true,
  assignToGroups?: RuleGroup[]
) => {
  const results = useQueries({
    queries: rules.map((rule) => ({
      ...rulesKeys.calcApplyTo({
        rules: [rule],
        ...(assignToGroups ? { assign_to_groups: assignToGroups } : {}),
      }),
      enabled,
    })),
  });

  return results.map((result) => ({
    count: result.data?.employee_ids.length,
    isLoading: result.isLoading,
  }));
};
